import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Calendar, Plus, Check, X, Clock, AlertCircle,
  ChevronLeft, ChevronRight, Filter, RotateCcw, Building2, User, KeyRound, Users, CircleUser, LogOut as LogOutIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaveManagement = ({ user, onLogout }) => {
  const [leaves, setLeaves] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [restrictDialogOpen, setRestrictDialogOpen] = useState(false);
  const [therapists, setTherapists] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyTherapists, setPropertyTherapists] = useState([]);
  
  // 3-Step flow for therapists (like Attendance)
  const [step, setStep] = useState('loading'); // 'loading' | 'select' | 'pin' | 'leave'
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  
  // Admin filters
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterTherapist, setFilterTherapist] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Calendar view state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  const [restrictionForm, setRestrictionForm] = useState({
    property_id: 'all',
    therapist_id: 'all',
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  // Edit/Delete restriction dialog
  const [editRestrictionDialogOpen, setEditRestrictionDialogOpen] = useState(false);
  const [selectedRestriction, setSelectedRestriction] = useState(null);
  const [editRestrictionForm, setEditRestrictionForm] = useState({
    property_id: '',
    therapist_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const isAdmin = user?.role === 'super_admin';
  const isSharedLogin = propertyTherapists.length > 1;

  // Therapist name lookup
  const therapistMap = useMemo(() => {
    const map = {};
    therapists.forEach(t => {
      map[t.user_id] = t;
    });
    propertyTherapists.forEach(t => {
      map[t.user_id] = t;
    });
    return map;
  }, [therapists, propertyTherapists]);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch restrictions when property filter changes
  useEffect(() => {
    if (isAdmin && filterProperty) {
      fetchLeaveData();
    }
  }, [filterProperty]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (isAdmin) {
        // Admin goes directly to leave page
        const [leavesRes, restrictionsRes, therapistsRes, propsRes] = await Promise.all([
          axios.get(`${API}/leaves`, { headers }),
          axios.get(`${API}/leave-restrictions`, { headers }),
          axios.get(`${API}/therapists`, { headers }),
          axios.get(`${API}/properties`, { headers })
        ]);
        
        setLeaves(leavesRes.data);
        setRestrictions(restrictionsRes.data);
        setTherapists(therapistsRes.data.filter(t => t.status !== 'archived'));
        setProperties(propsRes.data.filter(p => p.status !== 'archived'));
        setStep('leave');
      } else {
        // Therapist: Fetch property therapists first
        const therapistsRes = await axios.get(`${API}/therapists`, { headers });
        const propTherapists = therapistsRes.data.filter(
          t => t.assigned_property_id === user.assigned_property_id && t.status !== 'archived'
        );
        setPropertyTherapists(propTherapists);
        
        // If only one therapist, auto-select and go to PIN
        if (propTherapists.length === 1) {
          setSelectedTherapist(propTherapists[0]);
          setStep('pin');
        } else {
          setStep('select');
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
      setStep('select');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build restriction query with property filter
      const restrictionParams = filterProperty && filterProperty !== 'all' 
        ? `?property_id=${encodeURIComponent(filterProperty)}` 
        : '';
      
      const [leavesRes, restrictionsRes] = await Promise.all([
        axios.get(`${API}/leaves`, { headers }),
        axios.get(`${API}/leave-restrictions${restrictionParams}`, { headers })
      ]);
      
      setLeaves(leavesRes.data);
      setRestrictions(restrictionsRes.data);
      
      // Fetch balance for selected therapist
      if (selectedTherapist) {
        try {
          const balanceRes = await axios.get(`${API}/leaves/balance/${selectedTherapist.user_id}`, { headers });
          setBalance(balanceRes.data);
        } catch (err) {
          // Balance endpoint might fail for new therapists
          setBalance({ remaining: 24, used: 0, annual_limit: 24 });
        }
      }
    } catch (error) {
      toast.error('Failed to load leave data');
    }
  };

  const handleTherapistSelect = (therapistId) => {
    const therapist = propertyTherapists.find(t => t.user_id === therapistId);
    setSelectedTherapist(therapist);
    setStep('pin');
    setPinValue('');
    setPinError('');
  };

  const handlePinVerify = async () => {
    if (pinValue.length !== 4) {
      setPinError('Please enter a 4-digit PIN');
      return;
    }
    
    setActionLoading(true);
    setPinError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Verify PIN
      await axios.post(
        `${API}/attendance/verify-pin`,
        {
          therapist_id: selectedTherapist.user_id,
          pin: pinValue
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // PIN verified - fetch leave data
      await fetchLeaveData();
      setStep('leave');
      toast.success(`Welcome, ${selectedTherapist.full_name?.split(' ')[0]}!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'PIN verification failed';
      if (errorMsg.includes('Invalid PIN')) {
        setPinError('Invalid PIN. Please try again.');
      } else {
        setPinError(errorMsg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackToSelect = () => {
    setStep('select');
    setSelectedTherapist(null);
    setPinValue('');
    setPinError('');
    setBalance(null);
  };

  // Filtered leaves for display - only show selected therapist's leaves
  const filteredLeaves = useMemo(() => {
    if (isAdmin) {
      return leaves.filter(leave => {
        if (filterProperty && filterProperty !== 'all' && leave.property_id !== filterProperty) return false;
        if (filterTherapist && filterTherapist !== 'all' && leave.therapist_id !== filterTherapist) return false;
        if (filterStatus && filterStatus !== 'all' && leave.status !== filterStatus) return false;
        return true;
      });
    }
    
    // For therapist, show only their leaves
    if (selectedTherapist) {
      return leaves.filter(l => l.therapist_id === selectedTherapist.user_id);
    }
    return [];
  }, [leaves, filterProperty, filterTherapist, filterStatus, isAdmin, selectedTherapist]);

  const resetFilters = () => {
    setFilterProperty('all');
    setFilterTherapist('all');
    setFilterStatus('all');
  };

  // Calculate minimum date (5 days from now)
  const minDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 5);
    return date.toISOString().split('T')[0];
  }, []);

  // Get restricted dates - filter by property and therapist
  const restrictedDates = useMemo(() => {
    const dates = new Set();
    const currentPropertyId = isAdmin ? filterProperty : user?.assigned_property_id;
    const currentTherapistId = isAdmin ? filterTherapist : selectedTherapist?.user_id;
    
    restrictions.forEach(r => {
      // Check if this restriction applies to current property
      const restrictionPropertyId = r.property_id || '';
      const restrictionTherapistId = r.therapist_id || '';
      
      // Skip if restriction is for a different property (when property filter is set)
      if (restrictionPropertyId && currentPropertyId && currentPropertyId !== 'all') {
        if (restrictionPropertyId !== currentPropertyId) {
          return; // Skip this restriction - it's for a different property
        }
      }
      
      // Skip if restriction is for a specific therapist that doesn't match current selection
      if (restrictionTherapistId && currentTherapistId && currentTherapistId !== 'all') {
        if (restrictionTherapistId !== currentTherapistId) {
          return; // Skip this restriction - it's for a different therapist
        }
      }
      
      // For therapist view, only show restrictions that apply to them
      if (!isAdmin && selectedTherapist) {
        // Skip if restriction is for a different property
        if (restrictionPropertyId && restrictionPropertyId !== selectedTherapist.assigned_property_id) {
          return;
        }
        // Skip if restriction is for a specific therapist that isn't this one
        if (restrictionTherapistId && restrictionTherapistId !== selectedTherapist.user_id) {
          return;
        }
      }
      
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.add(d.toISOString().split('T')[0]);
      }
    });
    return dates;
  }, [restrictions, isAdmin, filterProperty, filterTherapist, selectedTherapist, user?.assigned_property_id]);

  // Generate calendar days - filter by selected therapist
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startPadding = firstDay.getDay();
    
    // Add padding for days before the month starts
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, isPadding: true });
    }
    
    // Add all days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
      
      // Filter leaves by selected therapist (for therapist view)
      let dayLeaves = leaves.filter(l => l.start_date <= dateStr && l.end_date >= dateStr);
      if (!isAdmin && selectedTherapist) {
        dayLeaves = dayLeaves.filter(l => l.therapist_id === selectedTherapist.user_id);
      }
      
      const isRestricted = restrictedDates.has(dateStr);
      const isPast = new Date(dateStr) < new Date(new Date().toISOString().split('T')[0]);
      const isWithin5Days = new Date(dateStr) < new Date(minDate);
      
      days.push({
        date: d,
        dateStr,
        leaves: dayLeaves,
        isRestricted,
        isPast,
        isWithin5Days,
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  }, [currentMonth, leaves, restrictedDates, minDate, isAdmin, selectedTherapist]);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      const payload = { ...leaveForm };
      if (selectedTherapist) {
        payload.therapist_id = selectedTherapist.user_id;
      }
      
      await axios.post(`${API}/leaves`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Leave application submitted!');
      setApplyDialogOpen(false);
      setLeaveForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchLeaveData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply leave');
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/leaves/${leaveId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Leave ${action}d successfully`);
      fetchLeaveData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} leave`);
    }
  };

  const handleCreateRestriction = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      // Convert "all" values to empty strings for backend
      const submitData = {
        ...restrictionForm,
        property_id: restrictionForm.property_id === 'all' ? '' : restrictionForm.property_id,
        therapist_id: restrictionForm.therapist_id === 'all' ? '' : restrictionForm.therapist_id
      };
      await axios.post(`${API}/leave-restrictions`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Leave restriction created!');
      setRestrictDialogOpen(false);
      setRestrictionForm({
        property_id: 'all',
        therapist_id: 'all',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchLeaveData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create restriction');
    }
  };

  // Find restrictions for a specific date
  const getRestrictionsForDate = (dateStr) => {
    return restrictions.filter(r => {
      const start = r.start_date;
      const end = r.end_date;
      return dateStr >= start && dateStr <= end;
    });
  };

  // Handle clicking on a restricted date (admin only)
  const handleRestrictedDateClick = (dateStr) => {
    if (!isAdmin) return;
    
    const dateRestrictions = getRestrictionsForDate(dateStr);
    if (dateRestrictions.length > 0) {
      // Open edit dialog with first restriction
      const r = dateRestrictions[0];
      setSelectedRestriction(r);
      setEditRestrictionForm({
        property_id: r.property_id || 'all',
        therapist_id: r.therapist_id || 'all',
        start_date: r.start_date,
        end_date: r.end_date,
        reason: r.reason || ''
      });
      setEditRestrictionDialogOpen(true);
    }
  };

  const handleUpdateRestriction = async (e) => {
    e.preventDefault();
    if (!selectedRestriction) return;
    
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        property_id: editRestrictionForm.property_id === 'all' ? '' : editRestrictionForm.property_id,
        therapist_id: editRestrictionForm.therapist_id === 'all' ? '' : editRestrictionForm.therapist_id,
        start_date: editRestrictionForm.start_date,
        end_date: editRestrictionForm.end_date,
        reason: editRestrictionForm.reason
      };
      
      await axios.put(`${API}/leave-restrictions/${selectedRestriction.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Restriction updated!');
      setEditRestrictionDialogOpen(false);
      setSelectedRestriction(null);
      fetchLeaveData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update restriction');
    }
  };

  const handleDeleteRestriction = async () => {
    if (!selectedRestriction) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/leave-restrictions/${selectedRestriction.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Restriction deleted!');
      setEditRestrictionDialogOpen(false);
      setSelectedRestriction(null);
      fetchLeaveData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete restriction');
    }
  };

  if (loading || step === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Step 1: Select Who You Are (for therapists)
  if (step === 'select' && !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/therapist">
                <Button variant="outline" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <h1 className="text-2xl font-serif text-foreground">Leave Management</h1>
            </div>
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOutIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Logout
            </Button>
          </div>
        </div>

        <div className="max-w-xl mx-auto px-4 py-8">
          <div className="glass rounded-2xl p-6 space-y-6" data-testid="select-therapist-step">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-2">Who are you?</h2>
              <p className="text-muted-foreground">
                Select your name and enter your PIN to view your leaves
              </p>
              <Badge variant="outline" className="mt-2">
                {user.assigned_property_id}
              </Badge>
            </div>
            
            <RadioGroup
              value={selectedTherapist?.user_id || ''}
              onValueChange={handleTherapistSelect}
              className="space-y-3"
            >
              {propertyTherapists.map(therapist => (
                <div
                  key={therapist.user_id}
                  className="flex items-center space-x-3 p-4 rounded-xl border-2 border-transparent hover:border-primary/30 bg-muted/30 cursor-pointer transition-all"
                  onClick={() => handleTherapistSelect(therapist.user_id)}
                  data-testid={`therapist-option-${therapist.user_id}`}
                >
                  <RadioGroupItem value={therapist.user_id} id={therapist.user_id} />
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CircleUser className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <Label htmlFor={therapist.user_id} className="font-medium text-foreground cursor-pointer">
                        {therapist.full_name}
                      </Label>
                      <p className="text-xs text-muted-foreground">{therapist.email}</p>
                    </div>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Enter PIN (for therapists)
  if (step === 'pin' && !isAdmin && selectedTherapist) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleBackToSelect} data-testid="back-button">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <h1 className="text-2xl font-serif text-foreground">Leave Management</h1>
            </div>
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOutIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Logout
            </Button>
          </div>
        </div>

        <div className="max-w-xl mx-auto px-4 py-8">
          <div className="glass rounded-2xl p-6 space-y-6" data-testid="pin-step">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-serif text-foreground mb-2">Enter Your PIN</h2>
              <p className="text-lg font-medium text-primary">{selectedTherapist.full_name}</p>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <Label className="text-sm text-muted-foreground">4-digit PIN</Label>
                <Input
                  type="password"
                  maxLength={4}
                  value={pinValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setPinValue(val);
                    setPinError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && pinValue.length === 4) {
                      handlePinVerify();
                    }
                  }}
                  className="mt-3 text-center text-3xl tracking-[0.5em] font-mono h-16 max-w-[200px] mx-auto"
                  placeholder="••••"
                  autoFocus
                  data-testid="pin-input"
                />
                {pinError && (
                  <p className="text-sm text-red-500 mt-3 flex items-center justify-center gap-1">
                    <X className="w-4 h-4" /> {pinError}
                  </p>
                )}
              </div>
              
              <Button
                className="w-full h-12"
                onClick={handlePinVerify}
                disabled={pinValue.length !== 4 || actionLoading}
                data-testid="verify-pin-btn"
              >
                {actionLoading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Leave Management Page (for verified therapist or admin)
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!isAdmin && isSharedLogin ? (
              <Button variant="outline" size="sm" onClick={handleBackToSelect} data-testid="back-to-select">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            ) : (
              <Link to={isAdmin ? "/admin" : "/therapist"}>
                <Button variant="outline" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
            )}
            <h1 className="text-2xl font-serif text-foreground">Leave Management</h1>
          </div>
          <div className="flex items-center gap-2">
            {!isAdmin && (
              <Button onClick={() => setApplyDialogOpen(true)} data-testid="apply-leave-btn">
                <Plus className="w-4 h-4 mr-2" /> Apply Leave
              </Button>
            )}
            {isAdmin && (
              <>
                <Button variant="outline" onClick={() => setRestrictDialogOpen(true)}>
                  <AlertCircle className="w-4 h-4 mr-2" /> Add Restriction
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOutIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Verified User Info (for therapist) */}
        {!isAdmin && selectedTherapist && (
          <div className="glass rounded-2xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-serif text-foreground">{selectedTherapist.full_name}</h2>
              <p className="text-sm text-muted-foreground">{user.assigned_property_id}</p>
            </div>
          </div>
        )}

        {/* Leave Balance (for therapist) */}
        {!isAdmin && balance && (
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-serif text-foreground mb-4">
              Your Leave Balance ({new Date().getFullYear()})
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-center">
                <p className="text-3xl font-bold text-green-600">{balance.remaining || 24}</p>
                <p className="text-sm text-muted-foreground">Remaining</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-center">
                <p className="text-3xl font-bold text-amber-600">{balance.used || 0}</p>
                <p className="text-sm text-muted-foreground">Used</p>
              </div>
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-center">
                <p className="text-3xl font-bold text-blue-600">{balance.annual_limit || 24}</p>
                <p className="text-sm text-muted-foreground">Annual Limit</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Filters */}
        {isAdmin && (
          <div className="glass rounded-2xl p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <Select value={filterProperty} onValueChange={setFilterProperty}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.hotel_name} value={p.hotel_name}>{p.hotel_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterTherapist} onValueChange={setFilterTherapist}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Therapists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Therapists</SelectItem>
                  {therapists.map(t => (
                    <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>
          </div>
        )}

        {/* Calendar View */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-serif text-foreground">Calendar</h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium min-w-[120px] text-center">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
            
            {calendarDays.map((day, index) => (
              <div
                key={index}
                onClick={() => isAdmin && day.isRestricted && !day.isPadding && handleRestrictedDateClick(day.dateStr)}
                className={`p-2 min-h-[80px] rounded-lg border transition-all ${
                  day.isPadding ? 'bg-transparent border-transparent' :
                  day.isToday ? 'bg-amber-50 border-amber-300' :
                  day.isRestricted ? 'bg-red-100 border-red-300 cursor-pointer hover:bg-red-200' :
                  day.isPast || day.isWithin5Days ? 'bg-gray-100 border-gray-200' :
                  'bg-white border-border/50 hover:border-primary/30'
                }`}
                title={day.isRestricted && isAdmin ? 'Click to edit restriction' : undefined}
              >
                {!day.isPadding && (
                  <>
                    <span className={`text-sm font-medium ${
                      day.isToday ? 'text-amber-700' :
                      day.isRestricted ? 'text-red-700' :
                      day.isPast || day.isWithin5Days ? 'text-gray-400' :
                      'text-foreground'
                    }`}>
                      {day.date}
                    </span>
                    <div className="mt-1 space-y-1">
                      {day.leaves?.slice(0, 2).map((leave, i) => (
                        <div
                          key={i}
                          className={`text-xs px-1 py-0.5 rounded truncate ${
                            leave.status === 'approved' ? 'bg-green-600 text-white' :
                            leave.status === 'pending' ? 'bg-amber-500 text-white' :
                            'bg-red-600 text-white'
                          }`}
                          title={`${therapistMap[leave.therapist_id]?.full_name || 'Unknown'} - ${leave.status}`}
                        >
                          {isAdmin ? (therapistMap[leave.therapist_id]?.full_name?.split(' ')[0] || '?') : leave.leave_type}
                        </div>
                      ))}
                      {day.leaves?.length > 2 && (
                        <div className="text-xs text-muted-foreground">+{day.leaves.length - 2} more</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend - Dark Colors */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-600"></div>
              <span className="text-sm text-muted-foreground">Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500"></div>
              <span className="text-sm text-muted-foreground">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-600"></div>
              <span className="text-sm text-muted-foreground">Rejected/Restricted</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-300"></div>
              <span className="text-sm text-muted-foreground">Past/Within 5 days</span>
            </div>
          </div>
        </div>

        {/* Leave Requests */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-serif text-foreground mb-4">
            {isAdmin ? 'Leave Requests' : 'My Leave Requests'}
          </h3>
          
          {filteredLeaves.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No leave requests found</p>
          ) : (
            <div className="space-y-3">
              {filteredLeaves.map((leave, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl border border-border/50 bg-white flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      leave.status === 'approved' ? 'bg-green-100' :
                      leave.status === 'pending' ? 'bg-amber-100' :
                      'bg-red-100'
                    }`}>
                      {leave.status === 'approved' ? <Check className="w-5 h-5 text-green-600" /> :
                       leave.status === 'pending' ? <Clock className="w-5 h-5 text-amber-600" /> :
                       <X className="w-5 h-5 text-red-600" />}
                    </div>
                    <div>
                      {isAdmin && (
                        <p className="font-medium text-foreground">
                          {therapistMap[leave.therapist_id]?.full_name || 'Unknown'}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        {leave.start_date} to {leave.end_date} • {leave.leave_type}
                      </p>
                      {leave.reason && (
                        <p className="text-xs text-muted-foreground mt-1">{leave.reason}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge className={
                      leave.status === 'approved' ? 'bg-green-100 text-green-700' :
                      leave.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }>
                      {leave.status}
                    </Badge>
                    
                    {isAdmin && leave.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-300 text-green-600 hover:bg-green-50"
                          onClick={() => handleLeaveAction(leave.id || leave._id || leave.leave_id, 'approve')}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleLeaveAction(leave.id || leave._id || leave.leave_id, 'reject')}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Apply Leave Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Apply for Leave</DialogTitle>
            {selectedTherapist && (
              <DialogDescription>
                Applying for: <span className="font-medium text-foreground">{selectedTherapist.full_name}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          
          <form onSubmit={handleApplyLeave} className="space-y-4">
            <div>
              <Label>Leave Type</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="emergency">Emergency Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                  min={minDate}
                  required
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                  min={leaveForm.start_date || minDate}
                  required
                />
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Note: Leave must be applied at least 5 days in advance. Maximum 2 consecutive days.
            </p>
            
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder="Reason for leave..."
                rows={3}
              />
            </div>
            
            <Button type="submit" className="w-full">Submit Application</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Restriction Dialog (Admin) */}
      <Dialog open={restrictDialogOpen} onOpenChange={setRestrictDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Leave Restriction</DialogTitle>
            <DialogDescription>
              Create a restriction period where no leaves can be applied
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateRestriction} className="space-y-4">
            <div>
              <Label>Property</Label>
              <Select 
                value={restrictionForm.property_id} 
                onValueChange={(v) => {
                  // When property changes to "all", reset therapist to "all"
                  if (v === 'all') {
                    setRestrictionForm({ ...restrictionForm, property_id: v, therapist_id: 'all' });
                  } else {
                    setRestrictionForm({ ...restrictionForm, property_id: v });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.hotel_name} value={p.hotel_name}>{p.hotel_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Therapist</Label>
              <Select 
                value={restrictionForm.therapist_id} 
                onValueChange={(v) => setRestrictionForm({ ...restrictionForm, therapist_id: v })}
                disabled={restrictionForm.property_id === 'all'}
              >
                <SelectTrigger className={restrictionForm.property_id === 'all' ? 'opacity-50' : ''}>
                  <SelectValue placeholder="All Therapists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Therapists</SelectItem>
                  {therapists
                    .filter(t => restrictionForm.property_id === 'all' || t.assigned_property_id === restrictionForm.property_id)
                    .map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {restrictionForm.property_id === 'all' && (
                <p className="text-xs text-muted-foreground mt-1">Select a specific property to choose individual therapists</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={restrictionForm.start_date}
                  onChange={(e) => setRestrictionForm({ ...restrictionForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={restrictionForm.end_date}
                  onChange={(e) => setRestrictionForm({ ...restrictionForm, end_date: e.target.value })}
                  min={restrictionForm.start_date}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Reason</Label>
              <Textarea
                value={restrictionForm.reason}
                onChange={(e) => setRestrictionForm({ ...restrictionForm, reason: e.target.value })}
                placeholder="Reason for restriction (e.g., Diwali peak season)"
                rows={3}
                required
              />
            </div>
            
            <Button type="submit" className="w-full">Create Restriction</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Restriction Dialog */}
      <Dialog open={editRestrictionDialogOpen} onOpenChange={setEditRestrictionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Restriction</DialogTitle>
            <DialogDescription>
              Modify or remove this leave restriction
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateRestriction} className="space-y-4">
            <div>
              <Label>Property</Label>
              <Select
                value={editRestrictionForm.property_id} 
                onValueChange={(v) => {
                  if (v === 'all') {
                    setEditRestrictionForm({ ...editRestrictionForm, property_id: v, therapist_id: 'all' });
                  } else {
                    setEditRestrictionForm({ ...editRestrictionForm, property_id: v });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.hotel_name} value={p.hotel_name}>{p.hotel_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Therapist</Label>
              <Select 
                value={editRestrictionForm.therapist_id}
                onValueChange={(v) => setEditRestrictionForm({ ...editRestrictionForm, therapist_id: v })}
                disabled={editRestrictionForm.property_id === 'all'}
              >
                <SelectTrigger className={editRestrictionForm.property_id === 'all' ? 'opacity-50' : ''}>
                  <SelectValue placeholder="Select therapist" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Therapists</SelectItem>
                  {therapists
                    .filter(t => editRestrictionForm.property_id === 'all' || t.assigned_property_id === editRestrictionForm.property_id)
                    .map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {editRestrictionForm.property_id === 'all' && (
                <p className="text-xs text-muted-foreground mt-1">Select a specific property to choose individual therapists</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editRestrictionForm.start_date}
                  onChange={(e) => setEditRestrictionForm({ ...editRestrictionForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={editRestrictionForm.end_date}
                  onChange={(e) => setEditRestrictionForm({ ...editRestrictionForm, end_date: e.target.value })}
                  min={editRestrictionForm.start_date}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Reason</Label>
              <Textarea
                value={editRestrictionForm.reason}
                onChange={(e) => setEditRestrictionForm({ ...editRestrictionForm, reason: e.target.value })}
                placeholder="Reason for restriction"
                rows={3}
                required
              />
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Update Restriction</Button>
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDeleteRestriction}
              >
                Delete
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagement;
